import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Users, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { ScoreBadge, WhyList } from './BestFitPanel';

// ============================================================================
// Who actually fits this - the candidate pool ranked against THIS requirement
// ============================================================================
//
// WHAT THIS IS
// ------------
// Against the live "Collections Manager" requirement the old scoring code put
// a Cloud Support Associate, a Scrum Master, an IT Lead and a Security Analyst
// at 83% in the top ten, and not one collections person. Three of its five
// axes were near-constants, so the average floated everybody up.
//
// This list is scored right now by the rebuilt engine, which compares what the
// person does for a living against what the role is, and caps anyone from the
// wrong field. It never reads the stored match rows.
//
// The same four rules as the candidate-page panel: it disappears when empty,
// a failed fetch hides it rather than breaking the page, no number is ever
// shown without its reason and its confidence, and it writes nothing.

const C = {
  cream: '#faf7f0', card: '#ffffff', border: '#e5e1d5',
  ink: '#2b2b2b', muted: '#7a7a7a', gold: '#c9a84c', goldDark: '#b08d36',
  navy: '#2e3a68', warn: '#8a6d1f', warnBg: '#fdf6e3',
};
const FONT = "'Outfit', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const errText = (e) =>
  e?.response?.data?.detail || e?.message || 'Could not score this requirement';

export default function WhoFitsPanel({ requirementId, limit = 25, defaultOpen = true }) {
  const [data, setData] = useState(null);      // null = still asking
  const [open, setOpen] = useState(defaultOpen);
  const [strongOnly, setStrongOnly] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!requirementId) { setData({ results: [] }); return undefined; }
    let alive = true;
    setData(null); setErr('');
    const min = strongOnly ? 70 : 0;
    api.get(`/match-view/for-requirement/${encodeURIComponent(requirementId)}`
            + `?limit=${limit}&min_score=${min}`)
      .then((r) => { if (alive) setData(r.data || { results: [] }); })
      // A failure here must not break the requirement page around it.
      .catch((e) => { if (alive) { setErr(errText(e)); setData({ results: [] }); } });
    return () => { alive = false; };
  }, [requirementId, limit, strongOnly]);

  if (data === null) {
    return (
      <div style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
        fontFamily: FONT, marginBottom: 24, padding: '14px 16px',
        display: 'flex', alignItems: 'center', gap: 10,
        color: C.muted, font: `400 13px ${FONT}`,
      }}>
        <Loader2 size={15} />
        Scoring the candidate pool against this requirement…
      </div>
    );
  }

  const rows = data.results || [];

  // Nothing worth showing, or the call failed - render nothing at all.
  // (When "strong fits only" is on, an empty result is a real answer, so the
  //  panel stays up and says so rather than vanishing under the user.)
  if (err) return null;
  if (rows.length === 0 && !strongOnly) return null;

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
      fontFamily: FONT, marginBottom: 24, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '14px 16px', background: C.cream,
        borderBottom: open ? `1px solid ${C.border}` : 'none',
      }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, flex: 1,
            background: 'none', border: 'none', cursor: 'pointer',
            textAlign: 'left', padding: 0,
          }}
        >
          <Users size={17} color={C.goldDark} />
          <span style={{ font: `600 14px ${FONT}`, color: C.ink }}>
            Who actually fits this
          </span>
          <span style={{ font: `400 12px ${FONT}`, color: C.muted }}>
            {rows.length} shown of {data.considered ?? '—'} people · scored just now
          </span>
          <span style={{ marginLeft: 'auto' }}>
            {open ? <ChevronDown size={16} color={C.muted} />
                  : <ChevronRight size={16} color={C.muted} />}
          </span>
        </button>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          font: `400 12px ${FONT}`, color: C.muted, whiteSpace: 'nowrap',
        }}>
          <input
            type="checkbox"
            checked={strongOnly}
            onChange={(e) => setStrongOnly(e.target.checked)}
          />
          Strong fits only
        </label>
      </div>

      {open && (
        <div>
          {rows.length === 0 && (
            <div style={{
              padding: '16px', font: `400 13px ${FONT}`, color: C.muted,
            }}>
              Nobody in the pool reaches 70% on this requirement. That is a real
              answer, not a missing one - the people who look close on the older
              Matches screen are scoring low here because they are from a
              different line of work.
            </div>
          )}
          {rows.map((r, i) => (
            <div
              key={r.candidate_id || i}
              style={{
                padding: '12px 16px',
                borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  font: `600 12px ${FONT}`, color: C.muted, minWidth: 22,
                  paddingTop: 2,
                }}>
                  {i + 1}.
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: `600 13.5px ${FONT}`, color: C.navy }}>
                    {r.name || '(no name on file)'}
                  </div>
                  <div style={{ font: `400 12px ${FONT}`, color: C.muted, marginTop: 1 }}>
                    {[r.current_title, r.current_company].filter(Boolean).join(' · ')
                      || 'No job title on file'}
                  </div>
                </div>
                <ScoreBadge score={r.match_score} confidence={r.confidence} />
              </div>
              <div style={{ paddingLeft: 34 }}>
                <WhyList
                  why={r.why}
                  matched={r.matched_skills}
                  missing={r.missing_skills}
                />
              </div>
            </div>
          ))}
          <div style={{
            padding: '9px 16px', background: C.cream,
            borderTop: `1px solid ${C.border}`,
            font: `400 11.5px ${FONT}`, color: C.muted,
          }}>
            Scored live from the current candidate pool
            {typeof data.elapsed_ms === 'number' ? ` in ${data.elapsed_ms} ms` : ''}.
            Nothing here is saved, and the older figures on the Matches screen
            are untouched.
          </div>
        </div>
      )}
    </div>
  );
}
